import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PackageCheck, ShieldAlert, Truck } from 'lucide-react';
import { FilterHelpIconPanel } from '@/components/shared/FilterBar';
import { KpiCard } from '@/components/shared/KpiCard';
import { FilterBar } from '@/components/shared/FilterBar';
import { useFilterBarSelectAll } from '@/components/shared/filterBarSelectAll';
import { DataTable } from '@/components/shared/DataTable';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useCurrency } from '@/hooks/useCurrency';
import { useOrderTrackingDashboard } from '@/modules/orderTracking/hooks/useOrderTrackingDashboard';
import { OrderTrackingColumnPicker } from '@/modules/orderTracking/components/OrderTrackingColumnPicker';
import { OrderTrackingEmptyState } from '@/modules/orderTracking/components/OrderTrackingEmptyState';
import { OrderTrackingDetailSidebar } from '@/modules/orderTracking/components/OrderTrackingDetailSidebar';
import { SupplierPerformancePanel } from '@/modules/orderTracking/components/SupplierPerformancePanel';
import {
  DEFAULT_COLUMN_CONFIG,
  loadOrderTrackingColumnConfig,
  type OrderTrackingColumnId,
} from '@/modules/orderTracking/config/orderTrackingTableConfig';
import { sortOrdersBySeverity, getOrderExpectedDateSortValue } from '@/modules/orderTracking/utils/orderTrackingSort';
import type {
  OrderTrackingOrder,
  OrderTrackingOrderMetrics,
  OrderHealthStatus,
  ReceptionStatus,
} from '@/services/orderTracking/orderTracking.types';

type EnrichedOrder = OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics };

type SoFilter = 'all' | 'with_so' | 'without_so';

function normalizeHealthStatus(status: OrderHealthStatus): OrderHealthStatus {
  return status === 'partial' ? 'on_time' : status;
}

function getOrderHealthBadgeClasses(status: OrderHealthStatus): string {
  const s = normalizeHealthStatus(status);
  if (s === 'critical') return 'bg-red-100 text-red-800 border-red-200';
  if (s === 'late') return 'bg-red-50 text-red-700 border-red-200';
  if (s === 'at_risk') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'received') return 'bg-green-50 text-green-700 border-green-200';
  if (status === 'cancelled') return 'bg-gray-100 text-gray-700 border-gray-300';
  return 'bg-green-50 text-green-700 border-green-200';
}

function getOrderHealthLabel(status: OrderHealthStatus, t: TFunction): string {
  return t(`orderTracking.healthStatus.${normalizeHealthStatus(status)}`);
}

function getReceptionLabel(status: ReceptionStatus, t: TFunction): string {
  return t(`orderTracking.reception.${status}`);
}

const receptionClassMap: Record<ReceptionStatus, string> = {
  on_time: 'bg-green-50 text-green-700 border-green-200',
  at_risk: 'bg-amber-50 text-amber-700 border-amber-200',
  late: 'bg-red-50 text-red-700 border-red-200',
};

function computeSummaryFromOrders(orders: Array<OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }>) {
  const total = orders.length;
  const onTime = orders.filter((o) => normalizeHealthStatus(o.metrics.healthStatus) === 'on_time').length;
  const atRisk = orders.filter((o) => o.metrics.healthStatus === 'at_risk').length;
  const partial = 0;
  const critical = orders.filter((o) => o.metrics.healthStatus === 'critical').length;
  const delayed = orders.filter((o) => o.metrics.healthStatus === 'late' || o.metrics.healthStatus === 'critical').length;
  const leadTimeCompliance = total
    ? Math.round((orders.filter((o) => o.metrics.withinLeadTimeSla).length / total) * 100)
    : 0;
  const unitsInTransit = orders.reduce((acc, o) => acc + o.metrics.quantityPending, 0);
  const totalValueInTransit = orders.reduce(
    (acc, o) => acc + (o.metrics.pendingValue ?? o.metrics.totalValue),
    0
  );
  return {
    totalOrders: total,
    onTime,
    atRisk,
    partial,
    critical,
    delayed,
    leadTimeCompliance,
    unitsInTransit,
    totalValueInTransit,
  };
}

function formatDaysCell(metrics: OrderTrackingOrderMetrics): string {
  if ((metrics.daysLate ?? 0) > 0) return `+${metrics.daysLate}`;
  if (metrics.daysToDue != null) return String(metrics.daysToDue);
  return '—';
}

function ProgressPercentCell({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, percent));
  const barClass =
    clamped >= 70 ? 'bg-green-500' : clamped >= 40 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="w-full min-w-0 max-w-full">
      <div className="text-xs text-gray-600 mb-1">{clamped}%</div>
      <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className={`${barClass} h-full`} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

export function OrderTrackingPage() {
  const { t, i18n } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const warehouseId = useWarehouseId();
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const {
    data: dashboard,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: refetchDashboard,
  } = useOrderTrackingDashboard(warehouseId, {
    orderDateFrom: orderDateFrom || undefined,
    orderDateTo: orderDateTo || undefined,
  });
  const [supplierFilters, setSupplierFilters] = useState<string[]>([]);
  const [orderNumberFilters, setOrderNumberFilters] = useState<string[]>([]);
  const [healthFilters, setHealthFilters] = useState<string[]>([]);
  const [soFilter, setSoFilter] = useState<SoFilter>('all');
  const {
    selectAllByFilter,
    updateSelectAllFromChange,
    clearSelectAllFor,
    clearAllSelectAll,
  } = useFilterBarSelectAll();
  const [selectedOrder, setSelectedOrder] = useState<EnrichedOrder | null>(null);
  const [columnConfig, setColumnConfig] = useState<Record<string, boolean>>(DEFAULT_COLUMN_CONFIG);

  useEffect(() => {
    setColumnConfig(loadOrderTrackingColumnConfig());
  }, []);

  const loadError = isError
    ? t('orderTracking.loadError', {
        message: error instanceof Error ? error.message : 'unknown',
      })
    : null;
  const showInitialLoading = isLoading && !dashboard;

  const orders = dashboard?.orders ?? [];
  const supplierPerformance = dashboard?.supplierPerformance ?? [];
  const orderItemsByOrderId = dashboard?.orderItemsByOrderId ?? {};

  const suppliers = useMemo(() => Array.from(new Set(orders.map((o) => o.supplier))), [orders]);
  const orderNumbers = useMemo(
    () =>
      Array.from(new Set(orders.map((o) => o.orderNumber).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      ),
    [orders]
  );

  const trackedOrders = useMemo(
    () => orders.filter((order) => order.purchaseOrderStatus === 'PENDING' || order.purchaseOrderStatus === 'ORDERED'),
    [orders]
  );

  const enrichedOrders = useMemo(
    () =>
      trackedOrders.flatMap((order) => {
        if (!order.metrics) return [];
        return [{ ...order, metrics: order.metrics }];
      }),
    [trackedOrders]
  );

  const hasOrdersWithSo = useMemo(
    () => enrichedOrders.some((order) => order.metrics.linesWithSO > 0),
    [enrichedOrders]
  );

  const hasActiveTableFilters =
    supplierFilters.length > 0 ||
    orderNumberFilters.length > 0 ||
    healthFilters.length > 0 ||
    soFilter !== 'all';

  const filteredOrders = useMemo(
    () =>
      enrichedOrders.filter((order) => {
        const bySupplier = supplierFilters.length === 0 || supplierFilters.includes(order.supplier);
        const byOrderNumber =
          orderNumberFilters.length === 0 || orderNumberFilters.includes(order.orderNumber);
        const byHealth =
          healthFilters.length === 0 ||
          healthFilters.includes(normalizeHealthStatus(order.metrics.healthStatus));
        const bySo =
          soFilter === 'all' ||
          (soFilter === 'with_so' && order.metrics.linesWithSO > 0) ||
          (soFilter === 'without_so' && order.metrics.linesWithSO === 0);
        return bySupplier && byOrderNumber && byHealth && bySo;
      }),
    [enrichedOrders, supplierFilters, orderNumberFilters, healthFilters, soFilter]
  );

  const displayOrders = useMemo(() => sortOrdersBySeverity(filteredOrders), [filteredOrders]);

  useEffect(() => {
    setSelectedOrder((prev) => {
      if (!prev) return prev;
      const next = enrichedOrders.find((order) => order.id === prev.id);
      return next ?? null;
    });
  }, [enrichedOrders]);

  const handleToggleSupplier = (supplier: string) => {
    setSupplierFilters((prev) =>
      prev.includes(supplier) ? prev.filter((value) => value !== supplier) : [...prev, supplier]
    );
  };

  const handleClearAllFilters = useCallback(() => {
    setSupplierFilters([]);
    setOrderNumberFilters([]);
    setHealthFilters([]);
    setSoFilter('all');
    setOrderDateFrom('');
    setOrderDateTo('');
    clearAllSelectAll();
  }, [clearAllSelectAll]);

  const summary = useMemo(() => {
    if (!hasActiveTableFilters && dashboard?.summary) {
      return dashboard.summary;
    }
    return computeSummaryFromOrders(filteredOrders);
  }, [filteredOrders, dashboard?.summary, hasActiveTableFilters]);

  const healthFilterOptions = useMemo(
    () =>
      (['critical', 'late', 'at_risk', 'on_time'] as OrderHealthStatus[]).map((value) => ({
        value,
        label: getOrderHealthLabel(value, t),
      })),
    [t]
  );

  const healthFilterHelpList = useMemo(
    () => ({
      intro: t('orderTracking.filters.healthHelpIntro'),
      items: (['critical', 'late', 'at_risk', 'on_time'] as OrderHealthStatus[]).map((value) => ({
        label: getOrderHealthLabel(value, t),
        text: t(`orderTracking.filters.healthHelp.${value}`),
      })),
    }),
    [t]
  );

  const activeFilters = useMemo(() => {
    const supplier = supplierFilters.map((value) => ({ id: 'supplier', label: value, value }));
    const orderNumbersActive = orderNumberFilters.map((value) => ({
      id: 'order_number',
      label: value,
      value,
    }));
    const health = healthFilters.map((value) => ({
      id: 'health',
      label: getOrderHealthLabel(value as OrderHealthStatus, t),
      value,
    }));
    const dates: Array<{ id: string; label: string; value: string }> = [];
    if (orderDateFrom) {
      dates.push({
        id: 'order_date',
        label: `${t('orderTracking.filters.fromOrderDate')} ${orderDateFrom}`,
        value: 'from',
      });
    }
    if (orderDateTo) {
      dates.push({
        id: 'order_date',
        label: `${t('orderTracking.filters.toOrderDate')} ${orderDateTo}`,
        value: 'to',
      });
    }
    return [...supplier, ...orderNumbersActive, ...health, ...dates];
  }, [supplierFilters, orderNumberFilters, healthFilters, orderDateFrom, orderDateTo, t]);

  const orderColumns = useMemo(
    () => [
      {
        mandatory: true,
        header: 'PO',
        accessor: 'orderNumber',
        width: '100px',
        sortable: true,
        exportValue: (v: string) => v,
        cell: (v: string) => <span className="font-mono text-gray-900">{v}</span>,
      },
      {
        mandatory: true,
        header: t('orderTracking.table.supplier'),
        accessor: 'supplier',
        width: '160px',
        sortable: true,
        exportValue: (_v: string, i: OrderTrackingOrder) => i.supplier,
        cell: (v: string, i: OrderTrackingOrder) => (
          <div className="min-w-0">
            <p className="text-gray-900 font-medium truncate" title={v}>
              {v}
            </p>
            <p className="text-xs text-gray-500 truncate">{i.supplierCode}</p>
          </div>
        ),
      },
      {
        columnId: 'warehouse' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.warehouse'),
        accessor: 'warehouseCode',
        width: '100px',
        exportValue: (_v: string, i: OrderTrackingOrder) =>
          i.warehouseName ? `${i.warehouseCode} (${i.warehouseName})` : i.warehouseCode,
        cell: (_: string, i: OrderTrackingOrder) => (
          <div className="min-w-0">
            <p className="text-gray-900 truncate">{i.warehouseCode}</p>
            <p className="text-xs text-gray-500 truncate">{i.warehouseName}</p>
          </div>
        ),
      },
      {
        columnId: 'orderDate' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.orderDate'),
        accessor: 'orderDate',
        width: '82px',
        sortable: true,
        exportValue: (v: string) => v,
        cell: (v: string) => <span className="text-gray-600 whitespace-nowrap">{v}</span>,
      },
      {
        columnId: 'expectedDate' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.expectedDate'),
        accessor: 'expectedDate',
        width: '82px',
        sortable: true,
        sortValue: (_v: string | null, item: OrderTrackingOrder) => getOrderExpectedDateSortValue(item),
        exportValue: (v: string | null, item: OrderTrackingOrder) =>
          v ?? item.effectiveDeadline ?? '',
        cell: (v: string | null, item: OrderTrackingOrder) => (
          <span className="text-gray-600 whitespace-nowrap">
            {v ?? item.effectiveDeadline ?? '—'}
          </span>
        ),
      },
      {
        columnId: 'daysToDue' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.daysToDue'),
        accessor: 'id',
        width: '64px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          formatDaysCell(i.metrics),
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span
            className={`${(i.metrics.daysLate ?? 0) > 0 ? 'text-red-700 font-medium' : 'text-gray-900'}`}
          >
            {formatDaysCell(i.metrics)}
          </span>
        ),
      },
      {
        columnId: 'health' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.health'),
        accessor: 'id',
        width: '96px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          getOrderHealthLabel(i.metrics.healthStatus, t),
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${getOrderHealthBadgeClasses(i.metrics.healthStatus)}`}
          >
            {getOrderHealthLabel(i.metrics.healthStatus, t)}
          </span>
        ),
      },
      {
        columnId: 'progressUnits' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.progressUnits'),
        accessor: 'id',
        width: '100px',
        sortable: true,
        sortValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.progressUnits ?? i.metrics.progress ?? 0,
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.progressUnits ?? i.metrics.progress ?? 0,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <ProgressPercentCell percent={i.metrics.progressUnits ?? i.metrics.progress ?? 0} />
        ),
      },
      {
        columnId: 'progressProducts' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.progressProducts'),
        accessor: 'id',
        width: '100px',
        sortable: true,
        sortValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.progressProducts ?? 0,
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.progressProducts ?? 0,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <ProgressPercentCell percent={i.metrics.progressProducts ?? 0} />
        ),
      },
      {
        columnId: 'pendingProducts' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.pendingProducts'),
        accessor: 'id',
        width: '100px',
        sortable: true,
        sortValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.pendingProducts ?? 0,
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.pendingProducts ?? 0,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span className={i.metrics.pendingProducts ? 'text-gray-900 font-medium' : 'text-gray-500'}>
            {i.metrics.pendingProducts ?? 0}
          </span>
        ),
      },
      {
        columnId: 'qtyOrdered' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.qtyOrdered'),
        accessor: 'id',
        width: '78px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.quantityOrdered,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span className="tabular-nums">{i.metrics.quantityOrdered.toLocaleString(i18n.language)}</span>
        ),
      },
      {
        columnId: 'qtyReceived' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.qtyReceived'),
        accessor: 'id',
        width: '78px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.quantityReceived,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span className="tabular-nums">{i.metrics.quantityReceived.toLocaleString(i18n.language)}</span>
        ),
      },
      {
        columnId: 'qtyPending' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.qtyPending'),
        accessor: 'id',
        width: '78px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.quantityPending,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span
            className={`tabular-nums ${
              i.metrics.quantityPending > 0 ? 'text-amber-700 font-medium' : 'text-green-700 font-medium'
            }`}
          >
            {i.metrics.quantityPending.toLocaleString(i18n.language)}
          </span>
        ),
      },
      {
        columnId: 'pendingValue' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.pendingValue'),
        accessor: 'id',
        width: '88px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.pendingValue ?? i.metrics.totalValue,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span className="tabular-nums whitespace-nowrap">
            {formatMoney(i.metrics.pendingValue ?? i.metrics.totalValue)}
          </span>
        ),
      },
      {
        columnId: 'totalValue' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.totalValue'),
        accessor: 'id',
        width: '88px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.totalValue,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span className="tabular-nums whitespace-nowrap">{formatMoney(i.metrics.totalValue)}</span>
        ),
      },
      {
        columnId: 'salesOrder' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.salesOrder'),
        accessor: 'id',
        width: '56px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => {
          if (i.metrics.linesWithSO <= 0) return '';
          return `${i.metrics.linesWithSO}/${i.metrics.linesCount}`;
        },
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => {
          if (i.metrics.linesWithSO <= 0) return <span className="text-gray-400">—</span>;
          return (
            <span className="text-blue-700 font-medium">
              {i.metrics.linesWithSO}/{i.metrics.linesCount}
            </span>
          );
        },
      },
      {
        columnId: 'leadTime' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.leadTime'),
        accessor: 'id',
        width: '88px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          `${i.metrics.leadTimeRealDays}/${i.metrics.leadTimeTargetDays}`,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span className="whitespace-nowrap">
            {i.metrics.leadTimeRealDays}d /{' '}
            <span className="text-gray-500">{i.metrics.leadTimeTargetDays}d</span>
          </span>
        ),
      },
      {
        columnId: 'reception' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.reception'),
        accessor: 'id',
        width: '88px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          getReceptionLabel(i.metrics.receptionStatus, t),
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => {
          const rs = i.metrics.receptionStatus;
          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${receptionClassMap[rs]}`}>
              {getReceptionLabel(rs, t)}
            </span>
          );
        },
      },
      {
        columnId: 'linesCount' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.linesCount'),
        accessor: 'id',
        width: '72px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.linesCount,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span>{i.metrics.linesCount}</span>
        ),
      },
      {
        columnId: 'linesWithSO' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.linesWithSO'),
        accessor: 'id',
        width: '96px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.linesWithSO,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span>{i.metrics.linesWithSO}</span>
        ),
      },
      {
        columnId: 'linesWithoutSO' as OrderTrackingColumnId,
        mandatory: false,
        header: t('orderTracking.table.columns.linesWithoutSO'),
        accessor: 'id',
        width: '96px',
        exportValue: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) =>
          i.metrics.linesWithoutSO,
        cell: (_: number, i: OrderTrackingOrder & { metrics: OrderTrackingOrderMetrics }) => (
          <span>{i.metrics.linesWithoutSO}</span>
        ),
      },
    ],
    [t, i18n.language, formatMoney]
  );

  const visibleOrderColumns = useMemo(
    () =>
      orderColumns.filter((col: { mandatory?: boolean; columnId?: OrderTrackingColumnId }) => {
        if (col.columnId === 'salesOrder' && !hasOrdersWithSo) return false;
        return col.mandatory === true || !col.columnId || columnConfig[col.columnId] !== false;
      }),
    [orderColumns, columnConfig, hasOrdersWithSo]
  );

  const exportFilename = useMemo(() => {
    const date = new Date().toISOString().slice(0, 10);
    return `${t('orderTracking.ordersTable.exportFilename')}_${date}`;
  }, [t]);

  if (showInitialLoading) {
    return (
      <div className="py-10 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]" aria-label={t('orderTracking.loading')} />
      </div>
    );
  }

  if (loadError && !dashboard) {
    return (
      <div className="space-y-6">
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {loadError}
        </div>
      </div>
    );
  }

  const handleCriticalKpiClick = () => {
    setHealthFilters((prev) =>
      prev.length === 1 && prev[0] === 'critical' ? [] : ['critical']
    );
  };

  return (
    <>
      <div className="space-y-6 min-w-0 max-w-full">
        {isFetching && dashboard ? (
          <p className="text-xs text-gray-500" aria-live="polite">
            {t('orderTracking.refreshing')}
          </p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 min-w-0">
          <KpiCard
            title={t('orderTracking.kpis.totalOrders')}
            helpText={t('orderTracking.kpis.totalOrdersTooltip')}
            value={filteredOrders.length}
            isCount
            loading={isFetching && !dashboard}
            icon={<PackageCheck className="h-5 w-5" />}
          />
          <KpiCard
            title={t('orderTracking.kpis.critical')}
            helpText={t('orderTracking.kpis.criticalTooltip')}
            value={summary.critical}
            isCount
            loading={isFetching && !dashboard}
            variant={summary.critical > 0 ? 'danger' : 'default'}
            icon={<ShieldAlert className="h-5 w-5" />}
            onClick={handleCriticalKpiClick}
          />
          <KpiCard
            title={t('orderTracking.kpis.leadTimeCompliance')}
            helpText={t('orderTracking.kpis.leadTimeComplianceTooltip')}
            value={summary.leadTimeCompliance}
            isPercentage
            loading={isFetching && !dashboard}
            icon={<Truck className="h-5 w-5" />}
          />
          <KpiCard
            title={t('orderTracking.kpis.pendingValue')}
            helpText={t('orderTracking.kpis.pendingValueTooltip')}
            value={summary.totalValueInTransit}
            isEuro
            loading={isFetching && !dashboard}
            icon={<PackageCheck className="h-5 w-5" />}
          />
        </div>

        <FilterBar
          filters={[
            {
              id: 'order_number',
              name: t('orderTracking.filters.orderNumber'),
              options: orderNumbers.map((orderNumber) => ({ value: orderNumber, label: orderNumber })),
              multiple: true,
            },
            {
              id: 'supplier',
              name: t('orderTracking.filters.supplier'),
              options: suppliers.map((supplier) => ({ value: supplier, label: supplier })),
              multiple: true,
            },
            {
              id: 'health',
              name: t('orderTracking.filters.health'),
              options: healthFilterOptions,
              multiple: true,
              helpList: healthFilterHelpList,
            },
          ]}
          activeFilters={activeFilters}
          selectAllByFilter={selectAllByFilter}
          onFilterChange={(next, meta) => {
            updateSelectAllFromChange(next, meta);
            if (Object.prototype.hasOwnProperty.call(next, 'order_number')) {
              setOrderNumberFilters(next.order_number ?? []);
            }
            if (Object.prototype.hasOwnProperty.call(next, 'supplier')) setSupplierFilters(next.supplier ?? []);
            if (Object.prototype.hasOwnProperty.call(next, 'health')) setHealthFilters(next.health ?? []);
          }}
          onClearFilter={(id) => {
            if (id === 'order_number') {
              setOrderNumberFilters([]);
              clearSelectAllFor('order_number');
            }
            if (id === 'supplier') {
              setSupplierFilters([]);
              clearSelectAllFor('supplier');
            }
            if (id === 'health') {
              setHealthFilters([]);
              clearSelectAllFor('health');
            }
            if (id === 'order_date') {
              setOrderDateFrom('');
              setOrderDateTo('');
            }
          }}
          onClearAllFilters={handleClearAllFilters}
          extraContentInline
          extraContentColumns={2}
          extraContent={
            <>
              <div className="space-y-1">
                <label htmlFor="order-tracking-date-from" className="block text-xs font-medium text-gray-600">
                  {t('orderTracking.filters.fromOrderDate')}
                </label>
                <input
                  id="order-tracking-date-from"
                  type="date"
                  value={orderDateFrom}
                  max={orderDateTo || undefined}
                  onChange={(event) => setOrderDateFrom(event.target.value)}
                  className="mt-0.5 block w-full pl-2.5 pr-2 py-1.5 text-sm border border-gray-300 focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba] rounded-md bg-white"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-1">
                  <label htmlFor="order-tracking-date-to" className="block truncate text-xs font-medium text-gray-600">
                    {t('orderTracking.filters.toOrderDate')}
                  </label>
                  {(orderDateFrom || orderDateTo) && (
                    <button
                      type="button"
                      onClick={() => {
                        setOrderDateFrom('');
                        setOrderDateTo('');
                      }}
                      className="text-[11px] text-gray-500 hover:text-gray-700"
                    >
                      {t('filters.clear')}
                    </button>
                  )}
                </div>
                <input
                  id="order-tracking-date-to"
                  type="date"
                  value={orderDateTo}
                  min={orderDateFrom || undefined}
                  onChange={(event) => setOrderDateTo(event.target.value)}
                  className="mt-0.5 block w-full pl-2.5 pr-2 py-1.5 text-sm border border-gray-300 focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba] rounded-md bg-white"
                />
              </div>
            </>
          }
          extraFooter={
            <p className="text-xs text-gray-500">{t('orderTracking.filters.orderDateHelp')}</p>
          }
        />

        <SupplierPerformancePanel
          suppliers={supplierPerformance}
          selectedSuppliers={supplierFilters}
          onToggleSupplier={handleToggleSupplier}
        />

        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 space-y-4 min-w-0 max-w-full overflow-visible">
          <div className="relative z-20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">{t('orderTracking.ordersTable.title')}</h2>
              <FilterHelpIconPanel
                list={{
                  intro: t('orderTracking.ordersTable.helpIntro'),
                  items: [
                    { label: t('orderTracking.columnPicker.title'), text: t('orderTracking.columnPicker.description') },
                    { label: t('orderTracking.table.columns.expectedDate'), text: t('orderTracking.ordersTable.helpSort') },
                    { label: 'SO', text: t('orderTracking.ordersTable.helpSo') },
                  ],
                }}
              />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {hasOrdersWithSo && (
                <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
                  {(['all', 'with_so', 'without_so'] as SoFilter[]).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSoFilter(value)}
                      className={`px-3 py-1 text-xs font-medium rounded ${
                        soFilter === value
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {t(
                        `orderTracking.ordersTable.soFilter${value === 'all' ? 'All' : value === 'with_so' ? 'With' : 'Without'}`
                      )}
                    </button>
                  ))}
                </div>
              )}
              <OrderTrackingColumnPicker
                columnConfig={columnConfig}
                onChange={setColumnConfig}
                hiddenColumnIds={hasOrdersWithSo ? [] : ['salesOrder', 'linesWithSO', 'linesWithoutSO']}
              />
              <span className="text-sm text-gray-500">
                {displayOrders.length} {t('orderTracking.table.records')}
              </span>
            </div>
          </div>

          {enrichedOrders.length === 0 ? (
            <OrderTrackingEmptyState variant="no_orders" />
          ) : filteredOrders.length === 0 ? (
            <OrderTrackingEmptyState variant="no_filter_results" onClearFilters={handleClearAllFilters} />
          ) : (
            <div className="relative z-0 min-w-0 max-w-full overflow-x-auto">
              <DataTable
                columns={visibleOrderColumns}
                data={displayOrders}
                compact
                pagination
                allowExport
                exportFilename={exportFilename}
                onRowClick={(item: EnrichedOrder) => setSelectedOrder(item)}
              />
            </div>
          )}
        </div>
      </div>

      {selectedOrder && (
        <OrderTrackingDetailSidebar
          order={selectedOrder}
          items={orderItemsByOrderId[selectedOrder.id] ?? []}
          onClose={() => setSelectedOrder(null)}
          onReceived={() => {
            void refetchDashboard();
          }}
        />
      )}
    </>
  );
}
